import {
  SITE_TITLE,
  SITE_NAME,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
} from './siteConfig';
import { projects } from '../data/projects';
import { getProjectByPath, getProjectBySlug } from '../data/projects';
import { getProjectCanonicalPath, getProjectByCanonicalPath } from './projectPaths';
import {
  buildPersonJsonLd,
  buildCreativeWorkJsonLd,
  buildCollectionPageJsonLd,
  buildWebSiteJsonLd,
} from './jsonLd';

function getProjectOgImage(project) {
  if (!project) return DEFAULT_OG_IMAGE;
  return project.heroImage || project.media || DEFAULT_OG_IMAGE;
}

function ogImageMimeType(imagePath) {
  if (!imagePath) return 'image/png';
  if (imagePath.endsWith('.webp')) return 'image/webp';
  if (imagePath.endsWith('.jpg') || imagePath.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/png';
}

const STATIC_PAGES = {
  '/': {
    title: SITE_TITLE,
    description: DEFAULT_DESCRIPTION,
    image: DEFAULT_OG_IMAGE,
    jsonLd: [buildWebSiteJsonLd(), buildPersonJsonLd()],
  },
  '/work': {
    title: `Work — ${SITE_NAME}`,
    description:
      'Full archive of creative projects: 3D environments, hybrid AI production, information design and web development.',
    image: getProjectOgImage(projects[0]),
    jsonLd: [buildCollectionPageJsonLd()],
  },
  '/about': {
    title: `About — ${SITE_NAME}`,
    description:
      'Independent creative director and developer since 2019. Design, 3D, AI-driven production and web development across Europe and the Middle East.',
    image: DEFAULT_OG_IMAGE,
    jsonLd: [buildPersonJsonLd()],
  },
  '/privacy': {
    title: `Privacy Policy — ${SITE_NAME}`,
    description:
      'Privacy policy for andreafagioli.com: how contact form data is collected, stored and processed.',
    image: DEFAULT_OG_IMAGE,
    jsonLd: [buildPersonJsonLd()],
  },
  '/shop': {
    title: `Shop — ${SITE_NAME}`,
    description: 'Digital products and resources. Coming soon.',
    image: DEFAULT_OG_IMAGE,
    noindex: true,
  },
  '/card': {
    title: `Card — ${SITE_NAME}`,
    description:
      'Digital business card for Andrea Fagioli. Save contact details, email and website in one tap.',
    image: DEFAULT_OG_IMAGE,
    noindex: true,
  },
  '/landingtest': {
    title: `Landing Test — ${SITE_NAME}`,
    description: 'Private landing page test. Not indexed.',
    image: DEFAULT_OG_IMAGE,
    noindex: true,
  },
};

function buildProjectSeo(project) {
  const description =
    project.blurb ||
    project.tagline ||
    (Array.isArray(project.intro) ? project.intro[0] : '') ||
    DEFAULT_DESCRIPTION;
  const image = getProjectOgImage(project);

  return {
    title: `${project.title} — ${SITE_NAME}`,
    description: description.slice(0, 160),
    image,
    imageType: ogImageMimeType(image),
    type: 'article',
    jsonLd: [buildCreativeWorkJsonLd(project)],
  };
}

function resolveProject(pathname) {
  return (
    getProjectByCanonicalPath(pathname) ||
    getProjectByPath(pathname) ||
    getProjectBySlug(pathname.replace(/^\//, ''))
  );
}

function buildProjectPageSeo(pathname, project) {
  const seo = buildProjectSeo(project);
  const canonicalPath = getProjectCanonicalPath(project);

  return {
    pathname,
    ...seo,
    imageType: seo.imageType || ogImageMimeType(seo.image),
    canonical: absoluteUrl(canonicalPath),
    ogType: 'article',
  };
}

export function getSeoForPath(pathname) {
  const staticPage = STATIC_PAGES[pathname];
  if (staticPage) {
    const image = staticPage.image || DEFAULT_OG_IMAGE;
    return {
      pathname,
      ...staticPage,
      image,
      imageType: ogImageMimeType(image),
      canonical: absoluteUrl(pathname),
      ogType: 'website',
    };
  }

  const project = resolveProject(pathname);
  if (project) {
    return buildProjectPageSeo(pathname, project);
  }

  return {
    pathname,
    title: `Page not found — ${SITE_NAME}`,
    description: DEFAULT_DESCRIPTION,
    image: DEFAULT_OG_IMAGE,
    canonical: absoluteUrl(pathname),
    ogType: 'website',
    noindex: true,
  };
}
